// BookTickets/SummaryBox/useSummaryData.ts
import { useMemo, useCallback } from 'react'
import { useBookingState } from '@booking/state/booking.context'

import { groupBy } from 'lodash'
import { useLocale, useTranslations } from 'next-intl'

import { dateFnsLocale, format as formatInLisbonTz } from '@booking/lib/date'
import {
  calcSessionTotal,
  cartHasLines,
  calcTotalForCart,
  ticketCount,
  cartHasVoucherLines,
  voucherCount,
} from '@booking/lib/cart'
import { formatPrice } from '@booking/lib/money'
import { addDays, endOfDay, startOfDay } from 'date-fns'
import { stageNumberOf } from '../../../lib/bookingConfig'

export const useSummaryData = () => {
  const locale = useLocale()
  const t = useTranslations('booking')
  const state = useBookingState()
  const config = state.config
  const cart = state.cart
  const quantities = state.quantities
  const voucherQuantities = state.voucherQuantities
  const session = state.session
  const stage = state.stage ?? 1
  const shake = useMemo(
    () => ticketCount(quantities) > 0 || Boolean(session),
    [quantities, session],
  )

  const shouldDiscountPrice = useMemo(() => {
    const endOfTomorrow = endOfDay(addDays(new Date(), 1))
    const sessionStart =
      cart && cart.type === 'standard' && cartHasLines(cart)
        ? cart.session.dateTime
        : session?.dateTime

    // discount only if session time is after end of tomorrow (advance booking discount)
    return sessionStart ? startOfDay(sessionStart) > endOfTomorrow : false
  }, [cart, session])

  const hasTickets = useMemo(
    () =>
      (stage >= stageNumberOf(config, 'customer') &&
        cart &&
        cart.type === 'standard' &&
        cartHasLines(cart)) ||
      ticketCount(quantities) > 0,
    [cart, quantities, stage, config],
  )

  const hasVouchers = useMemo(
    () =>
      (stage >= stageNumberOf(config, 'customer') && cartHasVoucherLines(cart)) ||
      voucherCount(voucherQuantities) > 0,
    [cart, voucherQuantities, stage, config],
  )

  // Derived ticket start
  const getTicketStart = useCallback(
    () =>
      stage >= stageNumberOf(config, 'customer') &&
      cart &&
      cart.type === 'standard' &&
      cartHasLines(cart)
        ? cart?.session?.dateTime
        : session?.dateTime,
    [cart, session, stage, config],
  )

  const ticketDate = useMemo(() => {
    const start = getTicketStart()
    if (!start) return ''
    const dateFormat = locale === 'en' ? 'do MMM yyyy' : 'dd MMMM yyyy'
    return formatInLisbonTz(start, dateFormat, { locale: dateFnsLocale(locale) })
  }, [locale, getTicketStart])

  const ticketTime = useMemo(() => {
    const start = getTicketStart()
    if (!start) return ''
    const timeFormat = locale === 'en' ? 'HH:mm aaa' : 'HH:mm'
    return formatInLisbonTz(start, timeFormat, { locale: dateFnsLocale(locale) })
  }, [locale, getTicketStart])

  const hasTicketDate = stage >= stageNumberOf(config, 'datetime') && Boolean(getTicketStart())
  const hasTicketTime = stage >= stageNumberOf(config, 'datetime') && Boolean(getTicketStart())

  const ticketLines = useMemo(() => {
    if (cart && cart.type !== 'standard') return []
    if (!cart || !cartHasLines(cart) || stage <= stageNumberOf(config, 'datetime')) {
      // For the voucher flow, show voucherQuantities instead of ticket quantities
      const preCartItems = config.context === 'voucher' ? state.voucherQuantities : quantities
      return preCartItems
        .filter((item) => item.quantity > 0)
        .map((item) => ({
          variant: item.category,
          quantity: item.quantity,
          price: undefined,
          normalPrice: undefined,
        }))
    }

    const grouped = groupBy(
      cart.lines.filter((line) => line.category && !line.voucher /*&& !line.physical*/),
      'category',
    )

    const ticketLines = Object.entries(grouped).map(([id, group]) => {
      const disc = config.ticketPriceFunc ? config.ticketPriceFunc : null
      let normalPrice = disc ? disc(group[0]?.unitValue) : null

      // dont discount price for default flow if not advanced booking
      if (config.context === 'default' && !shouldDiscountPrice) {
        normalPrice = null
      }

      return {
        variant: id,
        quantity: group.length,
        normalPrice: normalPrice,
        price: group[0]?.unitValue ?? 0,
      }
    })

    /*const physicalLines = cart.lines
      .filter((l) => l.physical)
      .map((line) => ({
        variant: line.productId,
        quantity: line.quantity,
        price: line.unitPrice,
      }))*/

    console.log(ticketLines)
    return [...ticketLines /*...physicalLines*/]
  }, [cart, quantities, state.voucherQuantities, stage, config, shouldDiscountPrice])

  const voucherLines = useMemo(() => {
    if (!cart || !cartHasVoucherLines(cart) || stage < stageNumberOf(config, 'customer')) {
      return state.voucherQuantities
        .filter((item) => item.quantity > 0)
        .map((item) => ({
          variant: item.category,
          quantity: item.quantity,
          price: undefined,
          normalPrice: undefined,
        }))
    }

    if (!cart.voucherLines) return []

    return cart.voucherLines.map((line) => ({
      variant: line.category,
      quantity: line.quantity,
      price: line.lineValue,
      normalPrice: undefined,
      hasVoucher: true,
    }))
  }, [cart, config, stage, state.voucherQuantities])

  const extrasLines = useMemo(() => {
    if (cart && cart.type !== 'standard') return []
    const extrasConfig = state.config?.extras
    if (!extrasConfig) return []

    if (stage <= stageNumberOf(config, 'datetime') || !cart) {
      return state.extras
        .filter((e) => e.quantity > 0)
        .map((e) => {
          const item = extrasConfig.items?.find((i) => i.category === e.category)
          return {
            variant: e.category,
            quantity: e.quantity,
            label: item ? t(item.labelKey) : e.category,
          }
        })
    }

    return cart.extraLines.map((line) => {
      const item = extrasConfig.items?.find((i) => i.category === line.category)
      return {
        variant: line.id,
        quantity: line.quantity,
        normalPrice: item?.price,
        price: line.unitValue ?? 0,
        label: item ? t(item.labelKey) : '',
      }
    })
  }, [state.extras, state.config?.extras, cart, config, stage, t])

  const hasExtras = extrasLines.length > 0
  const hasAddons = useMemo(
    () =>
      cart && cart.type === 'standard' && cart.addons && cart?.addons.length > 0 ? true : false,
    [cart],
  )

  const ticketTotal = useCallback(() => {
    if (cart && cart.type === 'voucher') return formatPrice(calcTotalForCart(cart) * 100)

    if (!cartHasLines(cart) && session)
      return formatPrice(calcSessionTotal({ session, quantities }))
    return cart ? formatPrice(calcTotalForCart(cart) * 100) : formatPrice(0)
  }, [cart, quantities, session])

  /*const shouldDiscount = useMemo(() => {
    const endOfTomorrow = endOfDay(addDays(new Date(), 1))
    const sessionStart = cart && cartHasLines(cart) ? cart.session.dateTime : session?.dateTime
    if (!sessionStart) return false

    // discount only if session time is after end of tomorrow (advance booking discount)
    return startOfDay(sessionStart) > endOfTomorrow
  }, [cart, session])*/

  const subTotal = useCallback(() => {
    const total = cart ? cart.total : 0

    const cartDiscount = cart?.discount ? (cart.discount.percentage ?? 0) : 0

    // add the discount value
    let sub = (total / (100 - cartDiscount)) * 100

    // add $3 * the amount of tickets
    const cartLines = cart && cart.type === 'standard' ? cart.lines.length : 0
    sub = sub + 3 * (cartLines ?? 0)

    return formatPrice(sub * 100)
  }, [cart])

  const subTotalBeforeVoucher = subTotal

  const discountValue = () => {
    const discount = shouldDiscountPrice ? 10 : 0
    const cartDiscount = cart?.discount ? (cart.discount.percentage ?? 0) : 0
    return discount + cartDiscount + '%'
  }

  /*const discountPriceMap = () => {
    const map = [
      { adv: 23.5, normal: 26.5 },
      { adv: 24.5, normal: 27.5 },
      { adv: 25.5, normal: 28.5 },
      { adv: 26.5, normal: 29.5 },
      { adv: 27.5, normal: 30.5 },
      { adv: 28.5, normal: 31.5 },
    ]
  }*/

  return {
    shake,
    ticketLines,
    hasTickets,
    hasVouchers,
    hasTicketDate,
    ticketDate,
    hasTicketTime,
    ticketTime,
    voucherLines,
    extrasLines,
    hasAddons,
    hasExtras,
    ticketTotal,
    subTotal,
    subTotalBeforeVoucher,
    discountValue,
    shouldDiscountPrice,
    //availableFields,
  }
}

export type UseSummaryData = ReturnType<typeof useSummaryData>
